
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send } from 'lucide-react';

const WhatsAppIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
);

const WhatsAppButton = () => {
    const phoneNumber = "34644907194"; 
    const rotatingText = "• Contacto en Directo con nosotros Via Whatsapp •";
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState("");

    const handleSend = () => {
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
        setMessage("");
        setIsOpen(false);
    };
    
    return (
        <div className="fixed bottom-24 right-4 z-50 flex items-center justify-center">
            {/* Rotating Text Container */}
            <div className="absolute w-[150px] h-[150px] animate-spin-slow flex items-center justify-center pointer-events-none">
                <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" aria-hidden="true">
                    <defs>
                        <path id="circlePath" d="M 50, 50 m -35, 0 a 35,35 0 1,1 70,0 a 35,35 0 1,1 -70,0" />
                    </defs>
                    <text className="fill-white font-bold text-[8.5px] tracking-[0.02em]">
                        <textPath xlinkHref="#circlePath" startOffset="50%" textAnchor="middle">
                            {rotatingText}
                        </textPath>
                    </text>
                </svg>
            </div>

            {/* The Actual Button */}
            <button 
                onClick={() => setIsOpen(true)}
                className="pointer-events-auto bg-[#25D366] hover:bg-[#20bd5a] text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110 active:scale-95 flex items-center justify-center border-2 border-white relative group cursor-pointer"
                aria-label="Contactar por WhatsApp"
            >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 rounded-full bg-[#25D366] blur-xl opacity-0 group-hover:opacity-40 transition-opacity" />
                <div className="relative transform transition-transform group-hover:rotate-12">
                    <WhatsAppIcon />
                </div>
            </button>

            {/* WhatsApp Modal */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsOpen(false)}
                            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] cursor-pointer"
                        />
                        
                        {/* Modal Content */}
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="fixed inset-[5%] md:inset-[5%_10%] lg:inset-[5%_15%] bg-[#0b141a] z-[101] rounded-2xl shadow-2xl border border-white/10 flex flex-col overflow-hidden"
                            style={{ width: '90vw', height: '90vh', left: '5vw', top: '5vh' }}
                        >
                            {/* Modal Header */}
                            <div className="bg-[#202c33] p-4 flex items-center justify-between border-b border-white/5">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                                        <WhatsAppIcon />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-lg">Negolan Proyectos</h3>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-2 h-2 bg-[#25D366] rounded-full animate-pulse" />
                                            <span className="text-white/60 text-xs">En línea</span>
                                        </div>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsOpen(false)}
                                    className="p-2 hover:bg-white/10 rounded-full text-white/70 hover:text-white transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Modal Body (Chat-like area) */}
                            <div className="flex-1 p-6 overflow-y-auto bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat bg-opacity-5">
                                <div className="max-w-2xl mx-auto space-y-4">
                                    <div className="bg-[#202c33] text-white p-3 rounded-lg rounded-tl-none shadow-md max-w-[80%]">
                                        <p className="text-sm">¡Hola! 👋</p>
                                        <p className="text-sm mt-1">¿En qué podemos ayudarte hoy? Escribe tu mensaje debajo y te atenderemos personalmente por WhatsApp.</p>
                                    </div>
                                    
                                    {message && (
                                        <div className="flex justify-end">
                                            <div className="bg-[#005c4b] text-white p-3 rounded-lg rounded-tr-none shadow-md max-w-[80%]">
                                                <p className="text-sm whitespace-pre-wrap">{message}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer (Input area) */}
                            <div className="bg-[#202c33] p-4 border-t border-white/5">
                                <div className="max-w-3xl mx-auto flex flex-col gap-4">
                                    <label htmlFor="wa-message" className="text-white/50 text-xs uppercase tracking-wider font-semibold">
                                        Tu mensaje para el equipo de Negolan
                                    </label>
                                    <textarea 
                                        id="wa-message"
                                        autoFocus
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Escribe un mensaje..."
                                        className="w-full h-40 bg-[#2a3942] text-white p-4 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#25D366] placeholder:text-white/20 text-lg"
                                    />
                                    <div className="flex justify-end items-center gap-4">
                                        <button 
                                            onClick={handleSend}
                                            disabled={!message.trim()}
                                            className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold py-3 px-8 rounded-full transition-all shadow-lg hover:shadow-[#25D366]/20"
                                        >
                                            <span>Enviar vía WhatsApp</span>
                                            <Send size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default WhatsAppButton;
